import { useState } from 'react';
import { marked } from 'marked';
import posts, { CATEGORIES } from './blogPosts';
import styles from './Blog.module.css';

// marked options: no raw-HTML passthrough (`mangle`/`headerIds` are marked's
// legacy options and don't apply here) — posts are hardcoded content shipped
// with the bundle (no CMS for v1), never user input, so the default
// (non-GFM-breaks) renderer is enough for this post's headings/bold/lists/
// tables/links.
marked.setOptions({ gfm: true });

// Plain date string ('YYYY-MM-DD', no time) — anchor to UTC midnight so the
// displayed date can't shift a day depending on the viewer's timezone.
const formatPostDate = (dateStr) => {
  const ms = new Date(`${dateStr}T00:00:00Z`).getTime();
  if (Number.isNaN(ms)) return dateStr;
  return new Date(ms).toLocaleDateString(undefined, { dateStyle: 'long', timeZone: 'UTC' });
};

/**
 * One post's summary row (title/date/author/read time) plus an
 * expand/collapse toggle that reveals the full rendered-markdown body. Each
 * post expands in place rather than navigating to its own URL — there's
 * only one post today; per-post permalinks are a straightforward future
 * extension if the list grows.
 */
const BlogPost = ({ post }) => {
  const [expanded, setExpanded] = useState(false);
  const bodyId = `blog-post-body-${post.id}`;
  const category = CATEGORIES[post.category];

  return (
    <article className={styles.blogPost}>
      <button
        type="button"
        className={styles.blogPostHeader}
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        aria-controls={bodyId}
      >
        <span className={styles.blogPostToggleIcon} aria-hidden="true">{expanded ? '−' : '+'}</span>
        <span className={styles.blogPostHeaderText}>
          <span className={styles.blogPostTitle}>{post.title}</span>
          <span className={styles.blogPostMeta}>
            {category && (
              <>
                <span className={styles.blogPostCategory}>{category.label}</span>
                {' · '}
              </>
            )}
            {formatPostDate(post.date)} · {post.author} · {post.readTimeMin} min read
          </span>
        </span>
      </button>
      {expanded && (
        <div
          id={bodyId}
          className={styles.blogPostBody}
          // Content is hardcoded, developer-authored markdown shipped with
          // the bundle (blogPosts.js) — never end-user input — so rendering
          // marked's output directly is safe here without a separate
          // sanitizer dependency.
          dangerouslySetInnerHTML={{ __html: marked.parse(post.bodyMarkdown) }}
        />
      )}
    </article>
  );
};

/**
 * Standalone /blog page (moved out of Market Health, which originally
 * hosted this in-page per its spec's own §6 Q1 "simpler for v1" call — the
 * maintainer decided the blog belongs at the site level, not scoped to one
 * tab). Renders the static list of posts, each expandable in place to show
 * its full markdown body. This is a general blog: posts are grouped by
 * category (see blogPosts.js) — Research today, with announcements/product
 * explainers expected to follow.
 */
const BlogPage = () => (
  <main className={styles.page}>
    <header className={styles.header}>
      <h1 className={styles.title}>Blog</h1>
      <p className={styles.intro}>
        Notes from the FunViz team — how we build and validate our tools,
        product updates, and more.
      </p>
    </header>
    <div className={styles.blogList}>
      {posts.map((post) => (
        <BlogPost key={post.id} post={post} />
      ))}
    </div>
  </main>
);

export default BlogPage;
